import React, { useEffect, useRef, useState, useCallback } from "react";
import "./IdleScreensaver.css";

/**
 * IdleScreensaver — listens for inactivity. After IDLE_MS (default 1 hour) of
 * no mouse / keyboard / touch / wake from the user, the screen fades to black.
 * On the next mouse-move, the curtain lifts and `onWake` fires (host triggers
 * the boot song + "good afternoon, sir" greeting flow).
 *
 * Auth-gated so it never blocks the login screen.
 */
const IDLE_MS = 60 * 60 * 1000; // 1 hour

export default function IdleScreensaver({ enabled = true, onWake }) {
  const [asleep, setAsleep] = useState(false);
  const timerRef = useRef(null);
  const wakeFiredRef = useRef(false);

  const arm = useCallback(() => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAsleep(true);
      wakeFiredRef.current = false;
    }, IDLE_MS);
  }, [enabled]);

  const wake = useCallback(() => {
    if (!asleep) { arm(); return; }
    setAsleep(false);
    if (!wakeFiredRef.current) {
      wakeFiredRef.current = true;
      try { onWake?.(); } catch (e) { console.warn("[idle] wake handler failed:", e?.message); }
    }
    arm();
  }, [asleep, onWake, arm]);

  useEffect(() => {
    if (!enabled) return undefined;
    arm();
    const handler = () => wake();
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "wheel"];
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((ev) => window.removeEventListener(ev, handler));
    };
  }, [enabled, arm, wake]);

  if (!enabled || !asleep) return null;
  return (
    <div className="idle-screensaver" data-testid="idle-screensaver" aria-hidden>
      <div className="idle-grid" />
      <div className="idle-hint">// SLEEPING — move mouse to wake RAW-DATA</div>
    </div>
  );
}
